/**
 * File upload constraints for variant registration by layer.
 * Used by CreateVariantPage and variant API validation.
 */

export const VARIANT_MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024; // 100 MB

export type LayerCode = 'G' | 'S' | 'L' | 'M' | 'W';

export interface LayerUploadConfig {
  mimeTypes: string[];
  extensions: string[];
  accept: string; // for input accept attribute
}

export const VARIANT_UPLOAD_BY_LAYER: Record<LayerCode, LayerUploadConfig> = {
  G: {
    mimeTypes: ['audio/mpeg', 'audio/wav', 'audio/x-wav'],
    extensions: ['.mp3', '.wav'],
    accept: '.mp3,.wav,audio/mpeg,audio/wav,audio/x-wav',
  },
  S: {
    mimeTypes: ['image/jpeg', 'image/png'],
    extensions: ['.jpg', '.jpeg', '.png'],
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
  },
  L: {
    mimeTypes: ['image/jpeg', 'image/png'],
    extensions: ['.jpg', '.jpeg', '.png'],
    accept: '.jpg,.jpeg,.png,image/jpeg,image/png',
  },
  M: {
    mimeTypes: ['video/mp4', 'video/quicktime'],
    extensions: ['.mp4', '.mov'],
    accept: '.mp4,.mov,video/mp4,video/quicktime',
  },
  W: {
    mimeTypes: ['video/mp4', 'video/quicktime'],
    extensions: ['.mp4', '.mov'],
    accept: '.mp4,.mov,video/mp4,video/quicktime',
  },
};

/** Layer code to API path segment for POST /assets/{path}/variants */
export const LAYER_TO_VARIANTS_PATH: Record<LayerCode, string> = {
  G: 'song',
  S: 'star',
  L: 'look',
  M: 'move',
  W: 'world',
};

export function isVariantLayer(layer: string): layer is LayerCode {
  return layer === 'G' || layer === 'S' || layer === 'L' || layer === 'M' || layer === 'W';
}

export function getVariantUploadConfig(layer: string): LayerUploadConfig | null {
  if (!isVariantLayer(layer)) return null;
  return VARIANT_UPLOAD_BY_LAYER[layer];
}

export function isFileAllowedForLayer(file: File, layer: string): boolean {
  const config = getVariantUploadConfig(layer);
  if (!config) return false;
  const ext = '.' + (file.name.split('.').pop() || '').toLowerCase();
  const mimeOk = config.mimeTypes.includes(file.type);
  const extOk = config.extensions.includes(ext);
  return (mimeOk || extOk) && file.size <= VARIANT_MAX_FILE_SIZE_BYTES;
}
